<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { flashMessage } from '@/utils/FlashMessages'
import { roleService, userService } from '@/core/services'
import { useStore } from '@/core/store'
import { User, UserCreateRequest } from '@/core/resources/User'
import Role from '@/core/resources/Role'
import router from '@/router'
import { useRoute } from 'vue-router'
import { popupUtil } from '@/utils/Popup'
import { handleError } from '@/utils/ErrorHandler'
import TextInput from '@/components/input/TextInput.vue'
import PasswordInput from '@/components/input/PasswordInput.vue'

const store = useStore()
const user = ref<User>(new User())
const plainPassword = ref<string>('')
const confirmPlainPassword = ref<string>('')
const allRoles = ref<Role[]>([])
const route = useRoute()

const id = ref(Number(route.params.id))

onMounted(() => {
  roleService
    .getAll(store.getAccessToken())
    .then((roles) => {
      allRoles.value = roles
    })
    .catch(handleError)

  if (!id.value) return

  userService
    .get(id.value, store.getAccessToken())
    .then((res) => {
      user.value = res
    })
    .catch(() => {
      router.push({ name: 'users' })
    })
})

const createUser = async () => {
  const confirmed = await popupUtil.confirm('Are you sure you want to create this user?')
  if (!confirmed) return

  const userCreateRequest = new UserCreateRequest(user.value, plainPassword.value, confirmPlainPassword.value)

  userService
    .create(userCreateRequest, store.getAccessToken())
    .then(() => {
      flashMessage.success('User created successfully')
      router.push({ name: 'users' })
    })
    .catch(handleError)
}

const editUser = async () => {
  const confirmed = await popupUtil.confirm('Are you sure you want to edit this user?')
  if (!confirmed) return

  userService
    .update(user.value, store.getAccessToken())
    .then(() => {
      flashMessage.success('User edited successfully')
      router.push({ name: 'users' })
    })
    .catch(handleError)
}
</script>

<template>
  <div class="container">
    <form novalidate>
      <h3 v-if="!id">Create a new user</h3>
      <h3 v-else>Edit user</h3>

      <div class="form-group">
        <TextInput v-model="user.username" :name="'username'" :id="'username'" :placeholder="'Username'" :type="'text'" :required="true" />

        <TextInput v-model="user.email" :name="'email'" :id="'email'" :placeholder="'Email'" :type="'email'" :required="true" />

        <h3>Roles</h3>
        <div class="roles">
          <span v-for="role in allRoles" :key="role.name" class="role">
            <input type="checkbox" :id="'role-' + role.name" v-model="user.roles" :value="role.name" />
            <label :for="'role-' + role.name">{{ role.name }}</label>
          </span>
        </div>

        <template v-if="!id">
          <h3>Password</h3>

          <PasswordInput v-model="plainPassword" :name="'password'" :id="'password'" :placeholder="'Password'" :required="true" />

          <PasswordInput
            v-model="confirmPlainPassword"
            :name="'password-confirm'"
            :id="'password-confirm'"
            :placeholder="'Confirm password'"
            :required="true" />
        </template>
      </div>

      <button type="button" class="submit-button" @click="createUser" v-if="!id">Create user</button>
      <button type="button" class="submit-button" @click="editUser" v-else>Update user</button>
    </form>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/styles/input.scss';
@import '@/assets/styles/form.scss';

.roles {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;

  .role {
    display: flex;
    gap: 8px;
    align-items: center;

    input[type='checkbox'] {
      transform: scale(1.4);
    }

    label {
      font-size: 1rem;
      user-select: none;
    }
  }
}
</style>
